<template>
  <div class="resource-category-page">
    <Header />
    <div class="content container" :class="{ 'no-technologies': !technologies.length }">
      <div v-if="technologies.length" class="technologies">
        <Slider
          dark
          :gutter="16"
          class="technologies-slider"
          class-name="library-technologies"
        >
          <div
            v-for="(t, i) in technologies" :key="i" slot="slide"
            class="col"
          >
            <nuxt-link class="block" :to="`/resources-center/${$route.params.category}/${t.slug ? `?technology=${t.slug}` : ''}`" :class="{ active: (!$route.query.technology && !t.slug) || (t.slug === $route.query.technology) }">
              {{ t.name }}
            </nuxt-link>
          </div>
        </Slider>
      </div>
      <div
        ref="description" class="body-1 description" :class="{ expanded: category.descriptionExpanded }"
        :style="{ '--height': category.descriptionHeight }"
      >
        <div v-html="category.description" />
      </div>
      <span v-if="category.descriptionExpandable" class="btn-expand" @click="category.descriptionExpanded = !category.descriptionExpanded">
        See {{ category.descriptionExpanded ? 'less' : 'more' }}
      </span>
      <div v-if="resources && resources.list.length" class="resources" :class="$route.params.category">
        <div class="row">
          <ResourceCard
            v-for="(resource, idx) in resources.list"
            :key="idx"
            slot="slide"
            :resource="resource"
            class="col col-3 md-4 sm-6"
            heading="h2"
          />
        </div>

        <Pagination
          v-if="resources.count > 20"
          :page="+$route.query.page || 1"
          :total="resources.count"
          :per-page="20"
          align="center"
        />
      </div>
      <div v-else class="no-results">
        <h3>0 Results</h3>
        <p>Sorry, there were no matches for your search term. Try using a different term.</p>
      </div>
      <template v-if="$route.params.category !== 'career-guides'">
        <div v-if="popular.length" class="most-popular slider">
          <Slider :disabled="popular.length <= 3" dark class-name="resources">
            <h3 slot="heading">
              Most Popular {{ category.name }}
            </h3>
            <p slot="description" class="caption description">
              {{ mostPopularDescription }}
            </p>
            <ResourceCard
              v-for="(resource, idx) in popular"
              :key="idx"
              slot="slide"
              :resource="resource"
              no-pin
              class="col col-3 md-4 sm-6"
              heading="h4"
            />
          </Slider>
        </div>
        <div class="most-popular order-col">
          <h3>Most Popular {{ category.name }}</h3>
          <p class="caption description">
            {{ mostPopularDescription }}
          </p>
          <div class="row body">
            <ResourceCard
              v-for="(resource, idx) in popular"
              :key="idx"
              :resource="resource"
              no-pin
              class="col col-3"
              heading="h4"
            />
          </div>
        </div>
      </template>
    </div>
    <PreFooter
      :title="`Join ${$c.text('students.count')}+ Students and Start Learning`"
      description="Learn from the best, develop an invaluable skillset, and secure a job in data and AI."
      button-title="Start Your Career"
      button-link="/signup/"
      :dark="true"
    />
  </div>
</template>

<script>
import global from '@itbpbg/lms-components/src/mixins/global';
import Slider from '@itbpbg/lms-components/src/components/Slider';
import Pagination from '@itbpbg/lms-components/src/components/Pagination';
import Header from '../../../components/Resources/Header';
import ResourceCard from '../../../components/Resources/ResourceCard';
import PreFooter from '../../../components/PreFooter';

export default {
  components: {
    Pagination,
    Slider,
    Header,
    ResourceCard,
    PreFooter,
  },
  mixins: [global],
  async asyncData({
    $axios, route, $config, redirect, error,
  }) {
    const { category = {}, resources = { count: 1, list: [] }, popular = [] } = await $axios.$get(`/resources/categories/${route.params.category}?cInfo=1&filter[technology]=${route.query.technology || ''}&page=${+route.query.page || 1}`).catch(() => redirect('/resources-center/'));

    const page = await $axios.$get(`/pages/${encodeURIComponent(route.path.replace(/^\/|\/$/g, ''))}`).catch(() => error({ statusCode: 404, message: 'Page not found' })) || {};
    page.fullPath = `${$config.siteUrl}${route.fullPath}`;

    return {
      category, resources, popular, page,
    };
  },
  data() {
    return {
      category: {},
      resources: { count: 1, list: [] },
      popular: [],
      heightTimer: null,
    };
  },
  head() {
    let { metaTitle } = this.page;
    const currentPage = +this.$route.query.page;
    if (currentPage > 1) {
      metaTitle += ` - Page ${currentPage}`;
    }
    const linkedData = this.$linkedDataLoad([
      { key: 'WebSite' },
      { key: 'WebPage', values: { ...this.page, metaTitle } },
    ]);

    const rels = this.paginationRels({ current: currentPage || 1, total: this.resources.count, perPage: 20 }) || [];

    return {
      titleTemplate: `${metaTitle}%s`,
      link: [
        { rel: 'canonical', href: this.canonicalURL() },
        ...rels,
      ],
      meta: this.metaProps({ ...this.page, metaTitle }),
      script: [linkedData],
    };
  },
  computed: {
    technologies() {
      const technologies = [
        { name: 'All', slug: '' },
        { name: 'Python', slug: 'python' },
        { name: 'Excel', slug: 'excel' },
        { name: 'SQL', slug: 'sql' },
        { name: 'R', slug: 'r' },
        { name: 'Tableau', slug: 'tableau' },
        { name: 'Power BI', slug: 'powerbi' },
        { name: 'Git and GitHub', slug: 'git' },
        { name: 'Jupyter', slug: 'jupyter' },
        { name: 'Theory', slug: 'theory' },
      ];
      if (this.$route.params.category === 'templates') {
        return technologies.filter((t) => !['tableau', 'powerbi', 'git', 'jupyter', 'theory'].includes(t.slug));
      }
      if (this.$route.params.category === 'course-notes') {
        return technologies.filter((t) => !['r', 'tableau', 'powerbi', 'git', 'jupyter'].includes(t.slug));
      }
      if (this.$route.params.category === 'infographics' || this.$route.params.category === 'career-guides') {
        return [];
      }
      if (this.$route.params.category === 'practice-exams') {
        return technologies.filter((t) => !['jupyter'].includes(t.slug));
      }

      return technologies;
    },
    mostPopularDescription() {
      const description = {
        1: 'Check out our most helpful downloadable data science course notes according to our students and expert team of instructors—available for free in PDF format.',
        2: 'Explore our top data science templates, recommended by our students and expert team of instructors—free to download.',
        3: 'Explore our top data science and AI infographics, recommended by our students and expert team of instructors—free to download.',
        6: 'Explore our top data science practice exams, recommended by our students and expert team of instructors—free to download.',
      };

      if (description[this.category.id]) {
        return description[this.category.id];
      }

      return 'Check out the best online resources for data science according to our students and expert team of instructors—available to download and use for free.';
    },
  },
  mounted() {
    this.$watch(({ $route: { query } }) => ({ technology: query.technology, page: query.page }), async () => {
      await this.fetchResources();
      this.$nextTick(() => {
        document.querySelector('.content').scrollIntoView({ block: 'start', behavior: 'smooth' });
      });
    });

    this.$nextTick(() => {
      this.calculateDescriptionsHeight(true);
    });

    window.addEventListener('resize', this.calculateDescriptionsHeight);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateDescriptionsHeight);
  },
  methods: {
    async fetchResources() {
      const { resources } = await this.$axios.$get(`/resources/categories/${this.$route.params.category}?filter[technology]=${this.$route.query.technology || ''}&page=${+this.$route.query.page || 1}`).catch(() => {});

      this.resources = resources;
    },
    calculateDescriptionsHeight(force = false) {
      let delay = 200;
      if (force) {
        delay = 0;
      }

      clearTimeout(this.heightTimer);

      this.heightTimer = setTimeout(() => {
        const catElement = this.$refs.description;
        if (catElement) {
          const paragraphHeight = catElement.offsetHeight;
          const contentHeight = catElement.querySelector('div').offsetHeight;

          this.$set(this.category, 'descriptionHeight', `${contentHeight}px`);

          if (contentHeight > paragraphHeight) {
            this.$set(this.category, 'descriptionExpandable', true);
            this.$set(this.category, 'descriptionExpanded', false);
          }
        }
      }, delay);
    },
  },
};
</script>

<style lang="scss">
.resource-category-page {
  background: var(--main-dark-shade-1);

  .content {
    padding-top: var(--spacing-36);

    .description {
      color: var(--main-light-shade-4);
      max-height: 54px;
      margin-bottom: 63px;
      overflow: hidden;

      p {
        &:first-child {
          margin-top: 0;
        }

        &:last-child {
          margin-bottom: 0;
        }
      }

      &[style*="--height"] {
        transition: max-height 0.3s ease-in-out;
      }

      &:has(+ .btn-expand) {
        margin-bottom: 0;
      }

      + .btn-expand {
        display: inline-block;
        color: var(--accent);
        cursor: pointer;
        margin-bottom: 36px;
        line-height: 27px;
        font-weight: 600;
        font-size: 18px;
      }

      &.expanded {
        max-height: var(--height, 1000px);
      }
    }

    .technologies .technologies-slider,
    .topics .topics-slider {
      &:after,
      &:before {
        width: 53px;
        height: 100%;
        background:
          linear-gradient(
            270deg,
            var(--main-light-shade-1) 0%,
            rgba(250, 250, 251, 0) 100%
          );
        position: absolute;
        z-index: 1;
        bottom: -34px;
        pointer-events: none;
        transition: all 1s ease;
      }

      &:not(.on-last):after {
        content: "";
        right: 0;
        background:
          linear-gradient(
            270deg,
            var(--main-dark-shade-1) 0%,
            rgba(250, 250, 251, 0) 100%
          );
      }

      &:not(.on-first):before {
        content: "";
        left: 0;
        background:
          linear-gradient(
            90deg,
            var(--main-dark-shade-1) 0%,
            rgba(250, 250, 251, 0) 100%
          );
      }
    }

    .technologies {
      .technologies-slider {
        margin-left: -25px;
        margin-right: -25px;
        padding: 0 25px 35px;
        position: relative;

        .slider-header {
          min-height: 33px;
        }
      }

      .row {
        margin-left: -8px;
        margin-right: -8px;
      }

      .col {
        flex: 0;
        padding-left: 12px;
        padding-right: 12px;
        min-width: unset;
      }

      .block {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 139px;
        height: 40px;
        line-height: 40px;
        background: var(--main-dark-light-2);
        box-shadow: 0 4px 8px rgba(0, 12, 31, 0.5);
        border-radius: 100px;
        font-weight: 500;
        color: var(--main-light);
        text-transform: capitalize;

        &:before {
          background: linear-gradient(135deg, #56BFC7 -1.67%, #339299 103%);
        }

        &:after {
          background: linear-gradient(135deg, #8CDAE3 15.23%, #339299 103%);
        }

        img {
          width: 40px;
          filter: drop-shadow(0 2px 10px rgba(0, 12, 31, 0.5));
        }

        &.active {
          background: var(--accent);
        }
      }
    }

    .resources:not(.slider-container) {
      padding-bottom: var(--spacing-100);
      border-bottom: 1px solid rgba(230, 231, 233, 0.2);

      &.career-guides {
        border-bottom: unset;
      }

      .col {
        margin-bottom: 48px;
      }

      .pagination {
        li {
          a {
            width: 28px;
            height: 28px;
            line-height: 30px;
            border: none;
            border-radius: 50%;

            &.active {
              background: var(--main-light);
              border-radius: 50%;
              color: var(--main-dark-shade-0);
            }
          }

          &.page-item a {
            display: inline-block;
          }
        }
      }
    }

    .no-results {
      color: var(--main-light);

      p {
        border-top: 1px solid rgba(255, 255, 255, 0.4);
        padding-top: 16px;
      }
    }

    .most-popular {
      padding-top: var(--spacing-80);
      padding-bottom: var(--spacing-108);

      &.order-col {
        display: none;
      }

      h3 {
        color: var(--main-light);
        margin: 0;
      }

      .description {
        margin: var(--spacing-16) 0 var(--spacing-36);
        color: var(--main-light-shade-4);
        font-size: 18px;
        line-height: 150%;
      }

      .controls {
        svg circle[fill] {
          fill: transparent !important;
        }
      }

      .swiper {
        padding-bottom: var(--spacing-12);
      }
    }

    &.no-technologies {
      padding-top: 180px;
    }
  }

  .pre-footer {
    padding-top: var(--spacing-120);

    .h2 {
      width: 80%;
      margin: auto;
      margin-bottom: var(--spacing-16);
    }

    .subtitle {
      width: 90%;
      margin: auto;
      color: var(--main-light);
      margin-bottom: var(--spacing-48);
    }

    .bg {
      height: 90px;
      margin-top: 115px;
    }
  }
}

@include bp($bp-mobile) {
  .resource-category-page {
    .content {
      &.no-technologies {
        padding-top: 203px;
      }
    }

    .content.container {
      .technologies .block {
        width: 99px;
        overflow: hidden;
        text-overflow: ellipsis;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 1;
        display: block;
        display: -webkit-box;
        text-align: center;
      }

      .technologies-slider {
        margin-right: -24px;
        margin-left: -24px;

        .slider-header {
          min-height: unset;
        }
      }

      .resources {
        padding-bottom: 44px;

        .pagination {
          margin-top: 30px;
        }

        .row .col {
          margin-bottom: var(--spacing-30);
        }
      }

      .body-1 {
        width: 100%;
      }

      .most-popular {
        padding-bottom: var(--spacing-80);

        &.slider {
          display: none;
        }

        &.order-col {
          display: block;
          padding-top: var(--spacing-60);

          .body .col {
            margin-bottom: var(--spacing-30);

            &:last-child {
              margin-bottom: 0;
            }
          }
        }

        .description {
          width: 100%;
          margin-bottom: var(--spacing-40);
          font-size: var(--spacing-16);
        }
      }
    }

    .pre-footer {
      padding-top: var(--spacing-80);

      .h2 {
        width: 100%;
        font-size: var(--spacing-26);
      }

      .subtitle {
        width: 100%;
        line-height: 150%;
      }

      .bg {
        height: 150px;
        margin-top: var(--spacing-80);
      }
    }
  }
}
</style>
